import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import { Process } from "@/components/home/Process";
import { CtaBand } from "@/components/site/CtaBand";
import { SITE } from "@/lib/site";

const VALUES = [
  ["Reliability", "We turn up when we say we will, and we keep you in the loop."],
  ["Honest advice", "We recommend what the job needs — nothing more."],
  ["Workmanship", "Neat, compliant work that's tested before we leave."],
  ["Respect", "For your home, your site, your tenants and your time."],
];

const Story = () => (
  <section data-testid="about-story" className="bg-porcelain py-24 sm:py-32">
    <div className="container-x grid gap-14 lg:grid-cols-12 items-start">
      <div className="lg:col-span-5 lg:sticky lg:top-28">
        <div className="relative aspect-[4/5] clip-notch overflow-hidden bg-ink">
          <img src="/images/ig/p10_9.jpg" alt="Marble bathroom with freestanding bath by Austyle Plumbing" className="absolute inset-0 h-full w-full object-cover" />
        </div>
      </div>
      <div className="lg:col-span-7">
        <Reveal><p className="eyebrow text-cobalt mb-5">Our story</p></Reveal>
        <h2 className="font-wide font-black uppercase text-4xl sm:text-5xl leading-[0.95] tracking-tight">
          <MaskLines lines={["30 years of", "Sydney plumbing."]} />
        </h2>
        <div className="mt-10 space-y-6 text-slate-700 text-base md:text-lg leading-relaxed max-w-2xl">
          <Reveal><p>Austyle Plumbing has been part of Sydney's trade community for {SITE.years} years. In that time we've plumbed new homes from the ground up, looked after busy strata buildings, kept commercial premises running and tackled heavy-duty industrial work.</p></Reveal>
          <Reveal><p>Our approach hasn't changed: do the job properly, communicate clearly, and treat every property as if it were our own. It's why builders, strata managers and homeowners keep calling us back.</p></Reveal>
          <Reveal><p className="text-slate-500 text-base">[Add your founder story, team names and milestones here.]</p></Reveal>
        </div>
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[["30", "Years experience"], ["3", "Sectors served"], ["SYD", "Sydney-wide"]].map(([n, l]) => (
            <div key={l} className="bg-white rounded-2xl shadow-[0_8px_30px_-12px_rgba(11,25,44,0.15)] p-6">
              <p className="font-wide font-black text-4xl">{n}</p>
              <p className="text-sm text-slate-600 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Values = () => (
  <section data-testid="about-values" className="bg-bone py-24 sm:py-32">
    <div className="container-x">
      <Reveal><p className="eyebrow text-cobalt mb-5">What we stand for</p></Reveal>
      <h2 className="font-wide font-black uppercase text-4xl sm:text-5xl leading-[0.95] tracking-tight"><MaskLines lines={["The Austyle", "standard."]} /></h2>
      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {VALUES.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.08} className="bg-white p-8 clip-notch-sm">
            <span className="block h-1 w-10 bg-brass" />
            <h3 className="font-wide font-extrabold text-2xl mt-6">{t}</h3>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">{d}</p>
          </Reveal>
        ))}
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {["/images/ig/p10_13.jpg", "/images/projects/hurstville/master-bath.jpg", "/images/ig/p10_15.jpg"].map((src, i) => (
          <Reveal key={src} delay={i * 0.1} className="relative aspect-[4/3] overflow-hidden bg-ink">
            <img src={src} alt="Austyle Plumbing workmanship" className="absolute inset-0 h-full w-full object-cover hover:scale-105 transition-transform duration-1000" />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <Seo title="About Austyle Plumbing – 30 Years of Sydney Plumbing" description="Meet Austyle Plumbing: 30 years of domestic, commercial and industrial plumbing across Sydney, from new builds and strata to everyday maintenance." />
      <PageHero testid="about-hero" eyebrow="About us" lines={["Built on", "30 years", "of trust."]} image="/images/ig/p10_4.jpg"
        intro="A Sydney plumbing business that has grown on word of mouth — by doing the job properly, every time." />
      <Story />
      <Values />
      <Process dark />
      <CtaBand title={["Let's talk about", "your next job."]} />
    </>
  );
}
